/** @jsx jsx */
import { jsx } from '@emotion/react';

import type { DescriptionPage_ProductVariant as DescriptionPageProductVariant } from '__generated__/graphql-types';

import { InlineNotification } from '@coursera/cds-core';

import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import { useIsEligibleForPlusMonthlyToAnnualUpgrade } from 'bundles/coursera-plus/utils/plusMonthlyToAnnualUpgradeUtils';
import { withEnrollButtonData } from 'bundles/enroll/components/xdp/BannerEnroll';
import withEnrollModal from 'bundles/enroll/components/xdp/withEnrollModal';
import type CoursesV1 from 'bundles/naptimejs/resources/courses.v1';
import EnrollButtonContent from 'bundles/unified-description-page-common/components/enroll/consumer/EnrollButtonContent';
import type { PropsFromEnrollHocs } from 'bundles/unified-description-page-common/components/enroll/consumer/EnrollButtonContent';
import { useCourseraPlusOwnership } from 'bundles/unified-description-page-common/contexts/consumer/CourseraPlusOwnershipContext';

import _t from 'i18n!nls/xdp';

type PropsFromCaller = {
  s12nId?: string;
  courseCount?: number;
  name: string;
  slug: string;
  isSpecialization: boolean;
  courseId?: string;
  course?: CoursesV1;
  s12nProductVariant?: DescriptionPageProductVariant;
  onStickyHeader?: boolean;
  hideFinaidLink?: boolean;
  onlyShowButton?: boolean;
};

// This component is used by the consumer course, specialization and professional certificate pages.
// We need to use existing enroll components so this component is wrapped by a few enroll HOCs that add data for the
// enroll modal. We take these props and give them to a common display component EnrollButtonContent that
// is also used for project pages.
const WrappedConsumerEnrollButton = ({
  onClick,
  buttonLabel,
  buttonLink,
  title,
  isEnrolled,
  isEligibleForPlusMonthlyToAnnualUpgrade,
  subtitleElem,
  buttonDisabled,
  enrollmentAvailableChoices,
  userAgent,
  TitleComponent,
  onMultipleLearningProgramsLinkClick,
  onStickyHeader,
}: PropsFromEnrollHocs) => {
  return (
    <EnrollButtonContent
      onClick={onClick}
      buttonLabel={buttonLabel}
      buttonLink={buttonLink}
      isEnrolled={isEnrolled}
      isEligibleForPlusMonthlyToAnnualUpgrade={isEligibleForPlusMonthlyToAnnualUpgrade}
      title={title}
      subtitleElem={subtitleElem}
      buttonDisabled={buttonDisabled}
      enrollmentAvailableChoices={enrollmentAvailableChoices}
      userAgent={userAgent}
      TitleComponent={TitleComponent}
      onMultipleLearningProgramsLinkClick={onMultipleLearningProgramsLinkClick}
      onStickyHeader={onStickyHeader}
    />
  );
};

// @ts-expect-error TSMIGRATION
const ButtonWithEnrollModal = withEnrollButtonData(true)(withEnrollModal(WrappedConsumerEnrollButton));

// Re-wrapping the enroll button with modal to clarify it's interface
// since the enroll modal components are not typed properly.
const ConsumerEnrollButtonNotConfigurable = ({
  s12nId,
  courseCount,
  name,
  slug,
  isSpecialization,
  courseId,
  course,
  s12nProductVariant,
  onStickyHeader,
  hideFinaidLink,
  onlyShowButton,
}: PropsFromCaller) => {
  const courseraPlusOwnership = useCourseraPlusOwnership();
  const isEligibleForPlusMonthlyToAnnualUpgrade = useIsEligibleForPlusMonthlyToAnnualUpgrade();
  return (
    <ErrorBoundaryWithTagging
      componentName="ConsumerEnrollButtonNotConfigurable"
      errorComponent={
        onlyShowButton || onStickyHeader ? undefined : (
          <InlineNotification
            severity="error"
            title={_t('An error occurred within our enrollment service')}
            role="alert"
          >
            {_t('Our development team has been informed and is actively working towards a resolution.')}
          </InlineNotification>
        )
      }
    >
      <ButtonWithEnrollModal
        s12nId={s12nId}
        courseCount={courseCount}
        name={name}
        slug={slug}
        isSpecialization={isSpecialization}
        courseId={courseId}
        course={course}
        s12nProductVariant={s12nProductVariant}
        onStickyHeader={onStickyHeader}
        hideFinaidLink={hideFinaidLink}
        isEligibleForPlusMonthlyToAnnualUpgrade={Boolean(
          courseraPlusOwnership?.isUserEligibleToSeeCta && isEligibleForPlusMonthlyToAnnualUpgrade
        )}
        onlyShowButton={onlyShowButton}
      />
    </ErrorBoundaryWithTagging>
  );
};

export default ConsumerEnrollButtonNotConfigurable;
